import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { masterRunbooksApi } from '@/features/master-runbooks/api'
import type { Classification } from '@/features/classifications/api'
import { namePatternFor, type ObjectDraft } from '../lib/template-graph'

/**
 * An object's properties. The name pattern's {prefix} is typed by whoever uses the template. Every
 * template object is exactly one object, so there is no "How many" and no {n} (Andrew,
 * 2026-09-22: "We don't have a number of objects, remove it"). The dialog
 * used to print a worked example with a sample prefix beneath the field; it read as an Object ID
 * that already existed, and there are no objects anywhere near a template (Andrew, 2026-09-18).
 *
 * The classification is not editable here. It decides the band, the legal edges and the icon, so
 * changing it is "delete this object and drop the other class" — which the canvas does honestly,
 * instead of silently invalidating the edges already drawn.
 */
export function ObjectEditorDialog({ object, classification, takenKeys, onSave, onDelete, onClose }: {
    object: ObjectDraft
    classification: Classification | null
    takenKeys: string[]
    onSave: (next: ObjectDraft) => void
    onDelete: () => void
    onClose: () => void
}) {
    const { t } = useTranslation()
    const [draft, setDraft] = useState<ObjectDraft>(object)
    useEffect(() => setDraft(object), [object])

    const { data: runbooks = [] } = useQuery({
        queryKey: ['master-runbooks', 'for-template-object'],
        queryFn: async () => {
            const r = await masterRunbooksApi.list({ perPage: 200 })
            return ((r.data as { data?: { id: number; description: string }[] }).data) || []
        },
        staleTime: 5 * 60 * 1000,
    })

    const keyClash = draft.ObjectKey.trim() !== object.ObjectKey && takenKeys.includes(draft.ObjectKey.trim())
    const problems = useMemo(() => {
        const list: string[] = []
        if (!draft.ObjectKey.trim()) list.push(t('templates.editor.errNoKey', 'Every object needs a key.'))
        if (keyClash) list.push(t('templates.editor.errDuplicateKey', 'Two objects share the key {{key}}.', { key: draft.ObjectKey.trim() }))
        if (!draft.NamePattern.includes('{prefix}')) {
            list.push(t('templates.editor.errNoPrefix', '{{key}} must include {prefix} in its name pattern.', { key: draft.ObjectKey.trim() }))
        }
        if (draft.NamePattern.includes('{n}')) {
            list.push(t('templates.editor.errHasN', '{{key}} is one object, so its name pattern has no {n} to number it.', { key: draft.ObjectKey.trim() }))
        }
        return list
    }, [draft, keyClash, t])

    return (
        <>
            <div className="fixed inset-0 z-[199] bg-black/20" onClick={onClose} aria-hidden />
            <div className="fixed left-1/2 top-1/2 z-[200] w-[460px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 space-y-3 rounded-lg border bg-background p-4 shadow-xl">
                <div>
                    <h2 className="text-sm font-semibold">{t('templates.editor.objectTitle', 'Object')}</h2>
                    <p className="text-xs text-muted-foreground">
                        {classification?.Name || t('templates.editor.classGone', 'Classification unavailable')}
                        {classification ? ` · ${t('templates.editor.band', 'Band')} ${classification.DisplayRank}` : ''}
                    </p>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="object-key">{t('templates.editor.objectKey', 'Key')} *</Label>
                    <Input
                        id="object-key"
                        value={draft.ObjectKey}
                        maxLength={40}
                        onChange={(e) => {
                            const ObjectKey = e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')
                            setDraft((d) => ({
                                ...d,
                                ObjectKey,
                                // Keep the pattern in step while it is still the generated one.
                                NamePattern: d.NamePattern === namePatternFor(d.ObjectKey)
                                    ? namePatternFor(ObjectKey)
                                    : d.NamePattern,
                            }))
                        }}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="object-pattern">{t('templates.editor.namePattern', 'Object ID pattern')} *</Label>
                    <Input
                        id="object-pattern"
                        value={draft.NamePattern}
                        maxLength={100}
                        onChange={(e) => setDraft((d) => ({ ...d, NamePattern: e.target.value }))}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="object-desc">{t('templates.editor.objectDescription', 'Object description')}</Label>
                    <Input
                        id="object-desc"
                        value={draft.DefaultDescription || ''}
                        maxLength={250}
                        placeholder={classification?.Name || ''}
                        onChange={(e) => setDraft((d) => ({ ...d, DefaultDescription: e.target.value }))}
                    />
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="object-runbook">{t('templates.editor.masterRunbook', 'Master runbook')}</Label>
                    <select
                        id="object-runbook"
                        className="h-9 w-full rounded-md border bg-background px-2 text-xs"
                        value={draft.MasterRunbookID ?? ''}
                        onChange={(e) => setDraft((d) => ({
                            ...d,
                            MasterRunbookID: e.target.value ? Number(e.target.value) : null,
                        }))}
                    >
                        <option value="">{t('templates.editor.noRunbook', 'None — the object starts without one')}</option>
                        {runbooks.map((rb) => (
                            <option key={rb.id} value={rb.id}>{rb.description}</option>
                        ))}
                    </select>
                </div>

                {/* Attach points are what bond the fragment into a real hierarchy when it is stamped
                    out: without one, the objects land unconnected to anything around them. */}
                <fieldset className="space-y-1.5 rounded border p-2">
                    <legend className="px-1 text-[11px] text-muted-foreground">
                        {t('templates.editor.attachLegend', 'Where this joins the hierarchy it is dropped into')}
                    </legend>
                    <label className="flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={draft.AttachUp}
                            onChange={(e) => setDraft((d) => ({ ...d, AttachUp: e.target.checked }))}
                        />
                        {t('templates.editor.attachUpLabel', 'Something above it can depend on this object')}
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                        <input
                            type="checkbox"
                            checked={draft.AttachDown}
                            onChange={(e) => setDraft((d) => ({ ...d, AttachDown: e.target.checked }))}
                        />
                        {t('templates.editor.attachDownLabel', 'This object can depend on something below it')}
                    </label>
                </fieldset>

                {problems.length > 0 && (
                    <ul className="space-y-0.5 text-[11px] text-destructive">
                        {problems.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                )}

                <div className="flex items-center justify-between border-t pt-3">
                    <Button variant="ghost" className="text-destructive" onClick={onDelete}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('templates.editor.removeObject', 'Remove object')}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
                        <Button
                            disabled={problems.length > 0}
                            onClick={() => onSave({ ...draft, ObjectKey: draft.ObjectKey.trim() })}
                        >
                            {t('common.apply', 'Apply')}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    )
}
